import _ from 'lodash';
import Tooltip from 'rc-tooltip';
import React from 'react';
import { DragDropContext, Draggable, Droppable } from 'react-beautiful-dnd';
import { BiCheck } from 'react-icons/bi';
import { CgReorder } from 'react-icons/cg';
import { FiChevronLeft, FiChevronRight, FiInfo, FiPlus, FiSearch } from 'react-icons/fi';
import { CircleSpinner } from 'react-spinners-kit';
import { useUserRoles } from '../../../util/use-user-role';
import { DashboardHeader } from '../../core/DashboardHeader';
import { DashboardWrapper } from '../../core/DashboardWrapper';
import { HorizontalContent } from '../../core/HorizontalContent';
import { Can } from '../../core/rbac/Can';
import { RhButton, RhButtonMode } from '../../core/RhButton';
import { RhCard } from '../../core/RhCard';
import { RhCardListItem } from '../../core/RhCardListItem';
import { RhInput } from '../../core/RhInput';
import { RhTabBar } from '../../core/RhTabBar';
import { colors, H2, H4, H5, Regular } from '../../core/Styles';
import {
  Number,
  PageInfo,
  PaginationButton,
  PaginationContainer,
  Programs,
  RightContent,
  SpinnerContainer,
  Studiers,
  Tags,
} from './BibleStudiesScreen.styles';
import { BibleStudiesProps } from '../containers/BibleStudiesContainer';

export const BibleStudiesScreen: React.FC<BibleStudiesProps> = props => {
  const { roles } = useUserRoles();
  return (
    <DashboardWrapper>
      <DashboardHeader
        title={
          <RhTabBar
            isDisabled={props.isLoading}
            activeId={props.currentTabId}
            tabs={props.tabs}
            onSelect={props.userSelectedTab}
          />
        }
      />
      <RhCard>
        <HorizontalContent
          left={[
            <H2 className="whitespace-nowrap">Bible Study Programs</H2>,
            props.isEditingOrder && (
              <div className="flex items-center space-x-1">
                <FiInfo className="text-yellow-400" />
                <H4>You may now drag and drop each item then click 'Save Order' to save to reorder.</H4>
              </div>
            ),
          ]}
          right={[
            props.isLoading && (
              <SpinnerContainer key="spinner">
                <CircleSpinner size={20} color={colors.primary} />
              </SpinnerContainer>
            ),
            <RhButton
              key="mine"
              title="My Studies"
              mode={props.isMyStudiesOnly ? RhButtonMode.primary : RhButtonMode.secondary}
              disabled={props.isLoading || props.isEditingOrder}
              onClick={props.userClickedMyStudies}
            />,
            <RhInput
              key="search"
              value={props.searchTerm}
              onChange={e => {
                props.userUpdatedSearchTerm(e.target.value);
              }}
              icon={FiSearch}
              placeholder="Search"
              disabled={props.isLoading || props.isEditingOrder}
            />,
            props.currentTabId === 'featured-studies' && (
              <Can
                key="order"
                role={roles?.[0]}
                perform="update:programs__mark_featured"
                yes={
                  props.isEditingOrder ? (
                    <div className="flex text-sm font-bold rounded-full bg-secondary text-primary">
                      <button
                        className="p-[10px] border-r border-highlight disabled:opacity-5 whitespace-nowrap"
                        disabled={props.isLoading}
                        onClick={props.userCanceledReorder}>
                        Cancel
                      </button>
                      <button
                        className="flex items-center p-[10px] disabled:opacity-5 whitespace-nowrap"
                        onClick={props.userClickedSaveOrder}
                        disabled={props.isLoading}>
                        <CgReorder style={{ marginRight: 4 }} />
                        Save Order
                      </button>
                    </div>
                  ) : (
                    <RhButton
                      title="Edit Order"
                      mode={RhButtonMode.hollow}
                      prefixIcon={CgReorder}
                      onClick={props.userClickedEditOrder}
                      disabled={props.isLoading}
                    />
                  )
                }
              />
            ),
            <Can
              key="add-btn"
              role={roles?.[0]}
              perform="create:programs"
              yes={<RhButton title="Add" suffixIcon={FiPlus} onClick={props.userClickedAdd} />}
            />,
          ]}
        />
        {!!props.bibleStudies?.length && (
          <DragDropContext onDragEnd={props.userEndedDrag}>
            <Droppable droppableId="chuchu">
              {(provided, snapshot) => (
                <Programs ref={provided.innerRef} {...provided.droppableProps}>
                  {props.bibleStudies!.map((program, i) => {
                    return (
                      <Draggable
                        key={program.id}
                        index={i}
                        draggableId={program.id}
                        isDragDisabled={props.currentTabId === 'all' || !props.isEditingOrder}>
                        {(provided, snapshot) => (
                          <div
                            ref={provided.innerRef}
                            {...provided.draggableProps}
                            {...provided.dragHandleProps}
                            style={{ ...provided.draggableProps.style }}>
                            <RhCardListItem
                              key={program.id}
                              isHoverable
                              imageUrl={program.imageUrl}
                              title={program.title}
                              author={_.startCase(program.authorName) || '-'}
                              authorImage={program.authorImage}
                              dateUpdated={program.dateUpdated}
                              description={program.description}
                              onClick={() => !props.isLoading && props.userClickedAStudy(program.id)}
                              trailing={() => (
                                <RightContent>
                                  <div className="flex flex-1">
                                    <div className="flex flex-col justify-between flex-1">
                                      <Tags>
                                        {program.tags.map((tag, index) => (
                                          <H4 key={tag + index} color={colors.normal}>
                                            #{tag}
                                          </H4>
                                        ))}
                                      </Tags>
                                      <Studiers>
                                        <H5>STUDIERS</H5>
                                        <Number>{program.numberOfStudiers}</Number>
                                      </Studiers>
                                    </div>
                                    <div className="flex flex-col items-end justify-between">
                                      <Can
                                        role={roles?.[0]}
                                        perform="update:programs__mark_featured"
                                        yes={
                                          <Tooltip
                                            placement="bottomRight"
                                            overlay={
                                              <Regular color={colors.background} weight="600">
                                                {program.isFeatured
                                                  ? 'Click to remove from Featured Studies'
                                                  : 'Click to add to Featured Studies'}
                                              </Regular>
                                            }>
                                            {program.isFeatured ? (
                                              <RhButton
                                                mode={RhButtonMode.secondary}
                                                style={{ padding: '6px 12px' }}
                                                disabled={props.isLoading}
                                                loading={props.isLoading}
                                                title={
                                                  <div className="flex items-center space-x-1">
                                                    <BiCheck size={24} color={colors.primary} />
                                                    <H2 color={colors.primary}>Featured</H2>
                                                  </div>
                                                }
                                                onClick={e => {
                                                  e.stopPropagation();
                                                  props.userToggledFeatureStatus(program.id, { isFeatured: false });
                                                }}
                                              />
                                            ) : (
                                              <RhButton
                                                mode={RhButtonMode.secondary}
                                                style={{ padding: '6px 12px' }}
                                                title="Add to Featured"
                                                disabled={props.isLoading}
                                                loading={props.isLoading}
                                                onClick={e => {
                                                  e.stopPropagation();
                                                  props.userToggledFeatureStatus(program.id, { isFeatured: true });
                                                }}
                                              />
                                            )}
                                          </Tooltip>
                                        }
                                        no={
                                          program.isFeatured && (
                                            <RhButton
                                              mode={RhButtonMode.secondary}
                                              style={{ padding: '6px 12px' }}
                                              title={
                                                <div className="flex items-center space-x-1">
                                                  <BiCheck size={24} color={colors.primary} />
                                                  <H2 color={colors.primary}>Featured</H2>
                                                </div>
                                              }
                                            />
                                          )
                                        }
                                      />
                                    </div>
                                  </div>
                                </RightContent>
                              )}
                            />
                          </div>
                        )}
                      </Draggable>
                    );
                  })}
                  {provided.placeholder}
                </Programs>
              )}
            </Droppable>
          </DragDropContext>
        )}

        {props.showPagination && !props.isEditingOrder && (
          <PaginationContainer>
            <PaginationButton
              onClick={props.userClickedPrevPage}
              disabled={props.isLoading || props.currentPage === 1}
              title="Previous page">
              <FiChevronLeft size={18} />
            </PaginationButton>

            <PageInfo>
              <Regular color={colors.grey} weight="600">
                Page {props.currentPage}
              </Regular>
            </PageInfo>

            <PaginationButton
              onClick={props.userClickedNextPage}
              disabled={props.isLoading || !props.hasNextPage}
              title="Next page">
              <FiChevronRight size={18} />
            </PaginationButton>
          </PaginationContainer>
        )}
      </RhCard>
    </DashboardWrapper>
  );
};
